import React, { useEffect, useState } from 'react';
import BreakCrumbs from '../../components/BreakCrumbs';
import { useHistory, useParams } from 'react-router-dom';
import {
  Box,
  Grid,
  Button,
  Paper,
  Typography,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@material-ui/core';
import { trim, map } from 'lodash';
import Swal from 'sweetalert2';
import SaveOutlinedIcon from '@material-ui/icons/SaveOutlined';
import KeyboardReturnOutlinedIcon from '@material-ui/icons/KeyboardReturnOutlined';
import { alertError } from '../../Util/Util';
import memberAPI from '../../middleware/memberAPI';
import storeAPI from '../../middleware/storeAPI';
import productPackageAPI from '../../middleware/productPackageAPI';
import {
  PayingMemberPlanStatus,
  PayingMemberPlanStatusText,
} from '../../data/common';

const PayingMemberPlan = (props) => {
  const { match } = props;
  const { id } = useParams();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPackages, setIsLoadingPackages] = useState(false);
  const [stores, setStores] = useState();
  const [allProductPackages, setAllProductPackages] = useState([]);
  const [isCreate, setIsCreate] = useState(() => {
    return trim(id) === '';
  });
  const [form, setForm] = useState({
    id: '',
    name: '',
    price: '',
    productPackageId: '',
    barcode: '',
    storeId: '',
    status: PayingMemberPlanStatus.ONLINE,
  });

  useEffect(() => {
    // 載入分店列表
    storeAPI.getAllStores().then(
      (res) => {
        setStores(res);
      },
      (error) => {
        alertError(error);
      },
    );

    // 載入所有套券列表（只在進入頁面時載入一次）
    setIsLoadingPackages(true);
    productPackageAPI
      .getProductPackages(1, 1000, { status: '' })
      .then(
        (rs) => {
          setAllProductPackages(rs.productPackageList || []);
        },
        (error) => {
          alertError(error);
        },
      )
      .then(() => {
        setIsLoadingPackages(false);
      });
  }, []);

  useEffect(() => {
    const checkIsCreateRs = trim(id) === '';
    setIsCreate(checkIsCreateRs);

    if (!checkIsCreateRs) {
      getPayingMemberPlan();
    }
  }, [id]);

  // 根據選擇的分店過濾套券
  const filteredProductPackages =
    trim(form.storeId) !== ''
      ? allProductPackages.filter(
          (pkg) => pkg.storeInfo && pkg.storeInfo.id === form.storeId,
        )
      : [];

  const getPayingMemberPlan = () => {
    setIsLoading(true);

    return memberAPI
      .getPayingMemberPlan(id)
      .then(
        (rs) => {
          const newForm = {
            id: rs.id || '',
            name: rs.name || '',
            price: rs.price || '',
            productPackageId: rs.productPackageId || '',
            barcode: rs.barcode || '',
            storeId: rs.storeInfo ? rs.storeInfo.id : '',
            status: rs.status ?? PayingMemberPlanStatus.ONLINE,
          };
          setForm(newForm);
        },
        (error) => {
          alertError(error);
        },
      )
      .then(() => {
        setIsLoading(false);
      });
  };

  const handleChangeForm = (name, value) => {
    // 如果是分店改變，清空套券選擇
    if (name === 'storeId') {
      setForm({
        ...form,
        storeId: value,
        productPackageId: '',
      });
    } else {
      setForm({
        ...form,
        [name]: value,
      });
    }
  };

  const handleSubmit = () => {
    // 驗證必填欄位
    if (trim(form.name) === '') {
      Swal.fire({
        icon: 'info',
        title: '請輸入方案名稱',
      });
      return;
    }

    if (trim(form.price) === '') {
      Swal.fire({
        icon: 'info',
        title: '請輸入價格',
      });
      return;
    }

    if (trim(form.storeId) === '') {
      Swal.fire({
        icon: 'info',
        title: '請選擇分店',
      });
      return;
    }

    setIsLoading(true);

    memberAPI
      .createOrUpdatePayingMemberPlan(form)
      .then(
        () => {
          Swal.fire({
            icon: 'success',
            title: '儲存完成',
          });

          if (isCreate) {
            history.push('/MemberManagement/PayingMemberPlanList');
          }
        },
        (error) => {
          alertError(error);
        },
      )
      .then(() => {
        setIsLoading(false);
      });
  };

  const handleCancel = () => {
    history.push('/MemberManagement/PayingMemberPlanList');
  };

  return (
    <div className="popular pagefadeIn">
      <BreakCrumbs path={match.url}></BreakCrumbs>

      <Box component={Paper} square elevation={2} p={1} mt={3} mb={3}>
        <Typography variant="h5" color="textPrimary" gutterBottom>
          {isCreate ? '新增 VIP 會員方案' : '編輯 VIP 會員方案'}
        </Typography>

        <Divider light />

        <Box p={3}>
          <Grid
            container
            direction="row"
            justify="space-between"
            alignItems="flex-start"
            spacing={2}>
            <Grid item xs={6}>
              <TextField
                required
                fullWidth={true}
                label="方案名稱"
                value={form.name || ''}
                onChange={(e) => {
                  handleChangeForm('name', e.target.value);
                }}
                disabled={isLoading}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth={true}
                label="價格"
                required
                type="number"
                value={form.price || ''}
                onChange={(e) => {
                  handleChangeForm('price', e.target.value);
                }}
                disabled={isLoading}
              />
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth required disabled={isLoading}>
                <InputLabel>分店</InputLabel>
                {stores && (
                  <Select
                    value={form.storeId || ''}
                    onChange={(e) => {
                      handleChangeForm('storeId', e.target.value);
                    }}
                    label="分店">
                    <MenuItem value="">請選擇分店</MenuItem>
                    {map(stores, (store) => {
                      return (
                        <MenuItem value={store.id} key={store.id}>
                          {store.name}
                        </MenuItem>
                      );
                    })}
                  </Select>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl
                fullWidth
                disabled={isLoading || isLoadingPackages || !form.storeId}>
                <InputLabel>套券</InputLabel>
                <Select
                  value={form.productPackageId || ''}
                  onChange={(e) => {
                    handleChangeForm('productPackageId', e.target.value);
                  }}
                  label="套券">
                  {!form.storeId ? (
                    <MenuItem value="" disabled>
                      請先選擇分店
                    </MenuItem>
                  ) : isLoadingPackages ? (
                    <MenuItem value="" disabled>
                      載入中...
                    </MenuItem>
                  ) : filteredProductPackages.length === 0 ? (
                    <MenuItem value="" disabled>
                      該分店無可用套券
                    </MenuItem>
                  ) : (
                    [
                      <MenuItem value="" key="empty">
                        請選擇套券
                      </MenuItem>,
                      ...map(filteredProductPackages, (pkg) => {
                        return (
                          <MenuItem value={pkg.id} key={pkg.id}>
                            {pkg.name}
                          </MenuItem>
                        );
                      }),
                    ]
                  )}
                </Select>
                {!form.storeId && (
                  <FormHelperText>請先選擇分店後再選擇套券</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth={true}
                label="條碼"
                value={form.barcode || ''}
                onChange={(e) => {
                  handleChangeForm('barcode', e.target.value);
                }}
                disabled={isLoading}
              />
            </Grid>

            <Grid item xs={6}>
              <FormControl fullWidth required disabled={isLoading}>
                <InputLabel>狀態</InputLabel>
                <Select
                  value={form.status}
                  onChange={(e) => {
                    handleChangeForm('status', e.target.value);
                  }}
                  label="狀態">
                  {Object.entries(PayingMemberPlanStatusText).map(
                    ([value, label]) => (
                      <MenuItem value={Number(value)} key={value}>
                        {label}
                      </MenuItem>
                    ),
                  )}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        <Divider light />

        <Box p={1} mt={2} mb={2} textAlign="right">
          <Button
            variant="contained"
            color="primary"
            size="large"
            style={{ marginRight: '24px' }}
            startIcon={<SaveOutlinedIcon />}
            disabled={isLoading}
            onClick={handleSubmit}>
            儲存
          </Button>

          <Button
            variant="outlined"
            size="large"
            startIcon={<KeyboardReturnOutlinedIcon />}
            disabled={isLoading}
            onClick={handleCancel}>
            取消
          </Button>
        </Box>
      </Box>
    </div>
  );
};

export default PayingMemberPlan;
